import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Flag from '@mui/icons-material/Flag';
import HourglassBottom from '@mui/icons-material/HourglassBottom';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import OpenInNew from '@mui/icons-material/OpenInNew';
import Shield from '@mui/icons-material/Shield';
import { ReactNode } from 'react';
import type { Theme } from '@mui/material/styles';
import BoxedIcon from '../../../../shared/boxed-icon';
import WarningCallout from './warning-callout';
import { ADMIN_CONSOLE_LABEL_URL } from '../label-locks';

/** Figma's rule icon size inside the 32px colored square. */
const RULE_ICON_SX = { width: 18, height: 18 };

// Google's own pages for each rule type. The Drive and Docs app id in the default
// classification link is the same for every domain.
export const DEFAULT_CLASSIFICATION_URL =
  'https://admin.google.com/ac/dc/appsettings/55656082996';
export const VAULT_RETENTION_URL = 'https://vault.google.com/retention/custom';
export const DATA_PROTECTION_URL = 'https://admin.google.com/ac/dp/rules';

interface RuleInfo {
  id: string;
  title: string;
  source: string;
  description: string;
  icon: ReactNode;
  steps: string[];
  linkText: string;
  href: string;
  extra?: ReactNode;
}

const RULES: RuleInfo[] = [
  {
    id: 'default-classification',
    title: 'Default Classification',
    source: 'Google Admin',
    description:
      'Use default classification to automatically apply labels to files in specific organizational units or admin groups.',
    icon: (
      <BoxedIcon variant="violet" size={32} icon={<Flag sx={RULE_ICON_SX} />} />
    ),
    steps: [
      'In Admin console, go to Apps → Google Workspace → Drive and Docs → Labels.',
      'Open Default classification and select an organizational unit or group.',
      'Choose this label and the field value new files should start with.',
    ],
    linkText: 'Manage default classification',
    href: DEFAULT_CLASSIFICATION_URL,
  },
  {
    id: 'vault-retention',
    title: 'Vault Retention',
    source: 'Vault',
    description:
      'Use labels in Vault retention rules to control how long items are kept before they are deleted.',
    // Figma's blue icon is one shade darker than BoxedIcon's default (info 600, not 500).
    icon: (
      <BoxedIcon
        variant="blue"
        size={32}
        icon={
          <HourglassBottom
            sx={{
              ...RULE_ICON_SX,
              color: (theme: Theme) => theme.palette.semantic.metricText.info,
            }}
          />
        }
      />
    ),
    steps: [
      'In Vault, open Retention → Custom rules → Create.',
      'Select Drive, set Specific labels, then choose this label and a retention period.',
      'Save the custom rule and confirm when prompted.',
    ],
    linkText: 'Manage vault retention',
    href: VAULT_RETENTION_URL,
  },
  {
    id: 'data-protection',
    title: 'Data Protection',
    source: 'Google Admin',
    description:
      'Use labels in data protection rules to let people know that items contain sensitive data.',
    // Figma's teal icon is lighter than BoxedIcon's default (teal 400 in the theme palette).
    icon: (
      <BoxedIcon
        variant="teal"
        size={32}
        icon={
          <Shield
            sx={{
              ...RULE_ICON_SX,
              color: (theme: Theme) => theme.palette.teal[400],
            }}
          />
        }
      />
    ),
    steps: [
      'In Admin console, go to Security → Access and data control → Data protection.',
      'Create a rule, choose Drive, and configure the conditions and actions.',
      'Review the rule and select Activate.',
    ],
    linkText: 'Manage data protection',
    href: DATA_PROTECTION_URL,
    extra: (
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {[
          {
            title: 'Label as condition',
            text: 'Block or warn on sensitive content when this label is applied.',
          },
          {
            title: 'Label as action',
            text: 'Apply this label automatically when sensitive content is detected.',
          },
        ].map((card) => (
          <Box key={card.title} sx={{ flex: 1 }}>
            <WarningCallout bordered fullHeight>
              {/* Only the fill and outline are warning-colored in the design; the text is not. */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: (theme) => theme.palette.semantic.text.title2,
                }}
              >
                {card.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: (theme) => theme.palette.semantic.text.support }}
              >
                {card.text}
              </Typography>
            </WarningCallout>
          </Box>
        ))}
      </Stack>
    ),
  },
];

interface StepRulesProps {
  /** Edit Label: a Google rule uses the label, so the "used in a Google rule" card shows. */
  usedInRule?: boolean;
  /** Edit Label: names of the fields a rule locks, shown as "Locked:" chips. */
  lockedFieldNames?: string[];
  /** Edit Label: the label's id, for its Admin console page. */
  labelId?: string;
}

const ruleLinkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
} as const;

/** The card telling the admin which Google rule uses the label, and where to manage it. */
function UsedInRuleCard({
  lockedFieldNames,
  labelId,
}: {
  lockedFieldNames: string[];
  labelId: string;
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        p: 1.5,
        bgcolor: 'action.hover',
        border: 1,
        borderColor: 'divider',
        borderRadius: '4px',
      }}
    >
      <InfoOutlined color="action" />
      <Stack spacing={1.5} sx={{ minWidth: 0 }}>
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              lineHeight: '24px',
              letterSpacing: '0.17px',
            }}
          >
            This label is used in a Google rule
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You can view and manage this rule in Google Admin or Vault. If you
            plan to change fields or options, it&apos;s a good idea to check the
            rule there first.
          </Typography>
        </Box>
        {lockedFieldNames.length > 0 && (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography variant="caption">Locked:</Typography>
            {lockedFieldNames.map((name) => (
              <Chip
                key={name}
                size="small"
                variant="outlined"
                icon={<LockOutlined sx={{ fontSize: 13 }} />}
                label={name}
                sx={{ bgcolor: 'background.paper' }}
              />
            ))}
          </Stack>
        )}
        <Stack direction="row" spacing={2}>
          <Link
            href={`${ADMIN_CONSOLE_LABEL_URL}${labelId}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            underline="hover"
            sx={ruleLinkSx}
          >
            Open Admin Console
            <OpenInNew sx={{ fontSize: 16 }} />
          </Link>
          <Link
            href={VAULT_RETENTION_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="caption"
            underline="hover"
            sx={ruleLinkSx}
          >
            Open Vault
            <OpenInNew sx={{ fontSize: 16 }} />
          </Link>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default function StepRules({
  usedInRule = false,
  lockedFieldNames = [],
  labelId = '',
}: StepRulesProps = {}) {
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Rules
        </Typography>
        <Typography variant="body2" color="text.secondary">
          After you publish, you can use this label in the Google rules below.
          These rules are set up in Google Admin and Vault.
        </Typography>
      </Box>
      {usedInRule && (
        <UsedInRuleCard lockedFieldNames={lockedFieldNames} labelId={labelId} />
      )}
      <Divider />
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        Here&apos;s how rules work
      </Typography>
      <Box>
        {RULES.map((rule) => (
          <Accordion
            key={rule.id}
            disableGutters
            // Like the design: a collapsed rule is a row with a divider; an expanded rule gets the
            // outline box, with a divider under its header.
            sx={{
              borderRadius: 0,
              border: 'none',
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              '&::before': { display: 'none' },
              '&.Mui-expanded': {
                border: (theme) =>
                  `1px solid ${theme.palette.semantic.outline.light}`,
                borderRadius: 1,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              // The theme shades accordion headers; the design keeps these on the card surface.
              sx={{
                bgcolor: 'background.paper',
                '&.Mui-expanded': {
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                },
              }}
              aria-controls={`${rule.id}-content`}
              id={`${rule.id}-header`}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                {rule.icon}
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontWeight: 500 }}>
                      {rule.title}
                    </Typography>
                    <Chip
                      label={rule.source}
                      size="small"
                      sx={{
                        borderRadius: '2px',
                        bgcolor: (theme) => theme.palette.semantic.surface.bg3,
                      }}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {rule.description}
                  </Typography>
                </Box>
              </Stack>
            </AccordionSummary>
            {/* Spacing from the design: 24px above and below, 32px in from the left, 16px between
                steps and before the link, 24px under the Data Protection cards. */}
            <AccordionDetails sx={{ px: 4, py: 3 }}>
              <Stack spacing={2}>
                {rule.extra && <Box sx={{ mb: 1 }}>{rule.extra}</Box>}
                <Stack component="ol" spacing={2} sx={{ m: 0, p: 0 }}>
                  {rule.steps.map((step, index) => (
                    <Stack
                      component="li"
                      key={step}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <Box
                        aria-hidden
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          bgcolor: (theme) =>
                            theme.palette.semantic.surface.info,
                          color: (theme) =>
                            theme.palette.semantic.metricText.info,
                          fontSize: 11,
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Typography variant="body2">{step}</Typography>
                    </Stack>
                  ))}
                </Stack>
                <Link
                  href={rule.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {rule.linkText}
                  <OpenInNew sx={{ fontSize: 16 }} />
                </Link>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Stack>
  );
}
